/* ==========================================================================
   UltraWheels — booking-form.js
   Three-step booking form (repair appointment, test ride). Everything posts in one Shopify contact
   form; the steps are purely a presentation layer, so without JS all panels
   stay visible and the form still submits in full.
   ========================================================================== */

(function () {
  'use strict';

  class BookingForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.panels = Array.from(this.querySelectorAll('[data-booking-panel]'));
      this.tabs = Array.from(this.querySelectorAll('[data-booking-tab]'));
      this.prevButton = this.querySelector('[data-booking-prev]');
      this.nextButton = this.querySelector('[data-booking-next]');
      this.submitButton = this.querySelector('[data-booking-submit]');
      this.dateInput = this.querySelector('[data-booking-date]');
      this.dateHint = this.querySelector('[data-booking-date-hint]');
      if (!this.form || this.panels.length === 0) return;

      this.closedDays = (this.dataset.closedDays || '')
        .split(',')
        .map((d) => parseInt(d.trim(), 10))
        .filter((d) => !Number.isNaN(d));

      this.step = 1;

      this.prevButton.addEventListener('click', () => this.go(this.step - 1));
      this.nextButton.addEventListener('click', () => this.next());
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.jumpTo(parseInt(tab.dataset.bookingTab, 10)));
      });

      if (this.dateInput) {
        this.dateInput.min = this.today();
        this.dateInput.addEventListener('change', () => this.validateDate());
      }

      // A failed server-side submit re-renders the page; land on the last step
      // so the visitor sees the error next to the button they pressed.
      const failed = this.querySelector('.form-status--error');
      this.go(failed ? this.panels.length : 1, { scroll: false });
    }

    today() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }

    validateDate() {
      if (!this.dateInput || !this.dateInput.value) return true;

      // Parsed as UTC, so read the UTC weekday to avoid a timezone off-by-one.
      const day = new Date(`${this.dateInput.value}T00:00:00Z`).getUTCDay();
      const closed = this.closedDays.includes(day);

      this.dateInput.setCustomValidity(closed ? this.dateInput.dataset.closedMessage || ' ' : '');
      if (this.dateHint) this.dateHint.classList.toggle('field__hint--error', closed);
      return !closed;
    }

    /* Only block progress on the fields of the current step. */
    stepIsValid() {
      const panel = this.panels[this.step - 1];
      const fields = Array.from(panel.querySelectorAll('input, select, textarea'));
      this.validateDate();

      for (const field of fields) {
        if (!field.checkValidity()) {
          field.reportValidity();
          return false;
        }
      }
      return true;
    }

    next() {
      if (!this.stepIsValid()) return;
      this.go(this.step + 1);
    }

    /* Tabs may only move backwards, or forwards through validated steps. */
    jumpTo(target) {
      if (target === this.step) return;
      if (target < this.step) {
        this.go(target);
        return;
      }
      while (this.step < target) {
        if (!this.stepIsValid()) return;
        this.go(this.step + 1, { scroll: false });
      }
      this.scrollIntoViewTop();
    }

    go(step, options) {
      const opts = options || {};
      this.step = Math.min(Math.max(step, 1), this.panels.length);
      const last = this.step === this.panels.length;

      this.panels.forEach((panel, index) => {
        const active = index === this.step - 1;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      this.tabs.forEach((tab, index) => {
        tab.classList.toggle('is-active', index === this.step - 1);
        tab.classList.toggle('is-done', index < this.step - 1);
      });

      this.prevButton.hidden = this.step === 1;
      this.nextButton.hidden = last;
      this.submitButton.hidden = !last;

      if (opts.scroll !== false) this.scrollIntoViewTop();
    }

    scrollIntoViewTop() {
      const offset = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
        10
      ) || 0;
      const top = window.scrollY + this.getBoundingClientRect().top - offset - 24;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }

  customElements.define('booking-form', BookingForm);
})();
